import React, { useEffect, useMemo, useState } from "react";
import API from "./api";
import { formatProfileNumberValue } from "./profileNumber";
import { getPolicySummary } from "./rbac";

const C = {
  card: "#ffffff",
  border: "rgba(185,52,58,0.14)",
  borderStrong: "rgba(185,52,58,0.20)",
  borderSoft: "rgba(185,52,58,0.10)",
  accent: "#D73C2E",
  accentDark: "#B9343A",
  accentLight: "#fff2ef",
  blue: "#B9343A",
  blueLight: "#fff7f5",
  text: "#252935",
  muted: "#6b6f7d",
  green: "#D73C2E",
  greenLight: "#fff2ef",
  amber: "#252935",
  amberLight: "#fff7f5",
  shadow: "rgba(37,41,53,0.08)",
  shadowLight: "rgba(37,41,53,0.07)",
  shadowAccent: "rgba(185,52,58,0.10)",
  shadowStrong: "rgba(37,41,53,0.30)",
};

export default function DuplicateMergeHistory({ token, canResolve = false, user = null }) {
  const [tab, setTab] = useState("cases");
  const [status, setStatus] = useState("pending");
  const [query, setQuery] = useState("");
  const [cases, setCases] = useState([]);
  const [history, setHistory] = useState([]);
  const [selectedCase, setSelectedCase] = useState(null);
  const [resolveAction, setResolveAction] = useState("");
  const [resolveNote, setResolveNote] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [error, setError] = useState("");
  const duplicatePolicy = useMemo(() => getPolicySummary(user, "duplicates", "view"), [user]);
  const hasDuplicatePolicyNotice = useMemo(() => (
    Number(duplicatePolicy?.max_results) > 0
      || duplicatePolicy?.denied
  ), [duplicatePolicy]);

  const authHeaders = useMemo(
    () => ({
      headers: {
        Authorization: `Bearer ${token || localStorage.getItem("token") || ""}`,
      },
    }),
    [token]
  );

  const stats = useMemo(() => {
    const pending = cases.filter((x) => normalizeStatus(x.status) === "pending").length;
    const retained = cases.filter((x) => ["retain", "retained"].includes(normalizeStatus(x.status))).length;
    const merged = cases.filter((x) => ["merge", "merged"].includes(normalizeStatus(x.status))).length;
    const replaced = cases.filter((x) => ["replace", "replaced"].includes(normalizeStatus(x.status))).length;
    return { pending, retained, merged, replaced, history: history.length };
  }, [cases, history]);

  const loadCases = async () => {
    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams();
      if (status) params.set("status", status);
      if (query.trim()) params.set("q", query.trim());
      params.set("include_meta", "1");

      const res = await API.get(`/duplicates/cases?${params.toString()}`, authHeaders);
      const rows = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.cases)
          ? res.data.cases
          : Array.isArray(res.data?.items)
            ? res.data.items
          : Array.isArray(res.data?.rows)
            ? res.data.rows
            : [];

      setCases(rows);
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to load duplicate cases");
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams();
      if (query.trim()) params.set("q", query.trim());
      params.set("include_meta", "1");

      const res = await API.get(`/duplicates/merge-history?${params.toString()}`, authHeaders);
      const rows = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.history)
          ? res.data.history
          : Array.isArray(res.data?.items)
            ? res.data.items
          : Array.isArray(res.data?.rows)
            ? res.data.rows
            : [];

      setHistory(rows);
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to load merge history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCases();
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (tab === "cases") loadCases();
    if (tab === "history") loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, tab]);

  const openCase = (item) => {
    setSelectedCase(item);
    setResolveAction("");
    setResolveNote("");
    setConfirmed(false);
    setError("");
  };

  const closeCase = () => {
    setSelectedCase(null);
    setResolveAction("");
    setResolveNote("");
    setConfirmed(false);
  };

  const resolveCase = async () => {
    if (!selectedCase?.id || !resolveAction || !confirmed) {
      setError("Choose an action and confirm review.");
      return;
    }

    setResolving(true);
    setError("");

    try {
      await API.post(
        `/duplicates/cases/${selectedCase.id}/resolve`,
        {
          action: resolveAction,
          note: resolveNote,
        },
        authHeaders
      );

      closeCase();
      await Promise.all([loadCases(), loadHistory()]);
      setTab(resolveAction === "retain" ? "cases" : "history");
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to resolve duplicate case");
    } finally {
      setResolving(false);
    }
  };

  return (
    <main style={S.page}>
      <style>{`
        @media (max-width: 920px) {
          .dmh-hero { flex-direction: column !important; align-items: flex-start !important; }
          .dmh-toolbar { flex-direction: column !important; align-items: stretch !important; }
          .dmh-stat-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
          .dmh-card-grid { grid-template-columns: 1fr !important; }
          .dmh-modal-body { grid-template-columns: 1fr !important; }
        }
      `}</style>

      <section style={S.hero} className="dmh-hero">
        <div>
          <p style={S.eyebrow}>Duplicate Operations</p>
          <h1 style={S.title}>Duplicate Merge History</h1>
          <p style={S.sub}>
            Review duplicate cases created and resolved during profile creation, then audit completed merges from
            <b> profile_merge_history</b>.
          </p>
        </div>

        <div style={S.heroActions}>
          <button type="button" style={S.ghostBtn} onClick={() => Promise.all([loadCases(), loadHistory()])}>
            Refresh
          </button>
        </div>
      </section>

      {hasDuplicatePolicyNotice && (
        <section style={S.policyNotice}>
          <strong style={S.policyNoticeTitle}>Duplicate access policy is active.</strong>
          <div style={S.policyNoticeBody}>
            {Number(duplicatePolicy?.max_results) > 0 && (
              <span>Lists are capped at {duplicatePolicy.max_results} rows per request.</span>
            )}
            {duplicatePolicy?.denied && (
              <span>Some duplicate operations are restricted by policy.</span>
            )}
          </div>
        </section>
      )}

      <section style={S.statGrid} className="dmh-stat-grid">
        <Stat label="Pending Cases" value={stats.pending} tone="rose" />
        <Stat label="Merged" value={stats.merged} tone="blue" />
        <Stat label="Replaced" value={stats.replaced} tone="amber" />
        <Stat label="Retained" value={stats.retained} tone="green" />
        <Stat label="History Rows" value={stats.history} tone="slate" />
      </section>

      <section style={S.panel}>
        <div style={S.tabs}>
          <button
            type="button"
            style={{ ...S.tabBtn, ...(tab === "cases" ? S.tabActive : {}) }}
            onClick={() => setTab("cases")}
          >
            Duplicate Cases
          </button>
          <button
            type="button"
            style={{ ...S.tabBtn, ...(tab === "history" ? S.tabActive : {}) }}
            onClick={() => setTab("history")}
          >
            Merge History
          </button>
        </div>

        <div style={S.toolbar} className="dmh-toolbar">
          <div style={S.searchWrap}>
            <input
              style={S.input}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search profile number e.g. M123, name, reason..."
              onKeyDown={(e) => {
                if (e.key === "Enter") tab === "cases" ? loadCases() : loadHistory();
              }}
            />
            <button
              type="button"
              style={S.searchBtn}
              onClick={() => (tab === "cases" ? loadCases() : loadHistory())}
              disabled={loading}
            >
              {loading ? "Loading..." : "Search"}
            </button>
          </div>

          {tab === "cases" && (
            <select style={S.select} value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All statuses</option>
              <option value="pending">Pending</option>
              <option value="merged">Merged</option>
              <option value="replaced">Replaced</option>
              <option value="retained">Retained</option>
            </select>
          )}
        </div>

        {error && <div style={S.error}>{error}</div>}

        {tab === "cases" ? (
          <CaseGrid cases={cases} loading={loading} onOpen={openCase} />
        ) : (
          <HistoryGrid history={history} loading={loading} />
        )}
      </section>

      {selectedCase && (
        <ResolveModal
          item={selectedCase}
          canResolve={canResolve}
          action={resolveAction}
          setAction={(value) => {
            setResolveAction(value);
            setConfirmed(false);
          }}
          note={resolveNote}
          setNote={setResolveNote}
          confirmed={confirmed}
          setConfirmed={setConfirmed}
          saving={resolving}
          error={error}
          onClose={closeCase}
          onSubmit={resolveCase}
        />
      )}
    </main>
  );
}

function CaseGrid({ cases, loading, onOpen }) {
  if (loading) return <div style={S.empty}>Loading duplicate cases...</div>;
  if (!cases.length) return <div style={S.empty}>No duplicate cases found.</div>;

  return (
    <div style={S.cardGrid} className="dmh-card-grid">
      {cases.map((item) => (
        <button type="button" key={item.id} style={S.caseCard} onClick={() => onOpen(item)}>
          <div style={S.cardTop}>
            <span style={{ ...S.statusPill, ...statusStyle(item.status) }}>
              {formatStatus(item.status)}
            </span>
            <b style={S.score}>{Math.round(Number(item.score || 0) * 100)}%</b>
          </div>

          <div style={S.profilePair}>
            <ProfileMini
              label="Primary"
              profileNo={item.primary_profile_no}
              name={item.primary_profile_name}
            />
            <span style={S.arrow}>→</span>
            <ProfileMini
              label="Duplicate"
              profileNo={item.duplicate_profile_no}
              name={item.duplicate_profile_name}
            />
          </div>

          <p style={S.reason}>{item.reason || "No reason captured."}</p>

          <div style={S.cardFooter}>
            <span>Case #{item.id}</span>
            <span>{formatDate(item.created_at)}</span>
          </div>
        </button>
      ))}
    </div>
  );
}

function HistoryGrid({ history, loading }) {
  if (loading) return <div style={S.empty}>Loading merge history...</div>;
  if (!history.length) return <div style={S.empty}>No merge history found.</div>;

  return (
    <div style={S.cardGrid} className="dmh-card-grid">
      {history.map((item) => (
        <article key={item.id} style={S.historyCard}>
          <div style={S.cardTop}>
            <span style={S.historyPill}>{item.merge_strategy || item.action || "manual"}</span>
            <b style={S.historyId}>#{item.id}</b>
          </div>

          <div style={S.profilePair}>
            <ProfileMini
              label="Canonical"
              profileNo={item.canonical_profile_no}
              name={item.canonical_profile_name}
            />
            <span style={S.arrow}>←</span>
            <ProfileMini
              label="Merged"
              profileNo={item.merged_profile_no}
              name={item.merged_profile_name}
            />
          </div>

          <div style={S.historyMeta}>
            <span>Duplicate case: #{item.duplicate_case_id || "—"}</span>
            <span>Merged by: {item.merged_by_email || item.merged_by || "—"}</span>
            <span>{formatDate(item.created_at || item.merged_at)}</span>
          </div>
        </article>
      ))}
    </div>
  );
}

function ResolveModal({
  item,
  canResolve,
  action,
  setAction,
  note,
  setNote,
  confirmed,
  setConfirmed,
  saving,
  error,
  onClose,
  onSubmit,
}) {
  const flow = getFlow(action);

  return (
    <div style={S.modalBackdrop}>
      <div style={S.resolveModal}>
        <div style={S.modalHead}>
          <div>
            <p style={S.eyebrow}>Resolve Duplicate Case #{item.id}</p>
            <h2 style={S.modalTitle}>Choose Retain / Merge / Replace</h2>
            <p style={S.modalSub}>{item.reason || "Review both profiles before confirming."}</p>
          </div>
          <button type="button" style={S.closeBtn} onClick={onClose}>×</button>
        </div>

        {error && <div style={S.error}>{error}</div>}
        {!canResolve && <div style={S.error}>You can view duplicate details, but your role does not have permission to resolve cases.</div>}

        <div style={S.modalBody} className="dmh-modal-body">
          <div style={S.compareBox}>
            <ProfileLarge
              label="Primary Profile"
              profileNo={item.primary_profile_no}
              name={item.primary_profile_name}
              id={item.primary_profile_id}
            />

            <div style={S.vsBadge}>VS</div>

            <ProfileLarge
              label="Duplicate Profile"
              profileNo={item.duplicate_profile_no}
              name={item.duplicate_profile_name}
              id={item.duplicate_profile_id}
            />
          </div>

          <div style={S.resolvePanel}>
            <p style={S.sectionTitle}>Action</p>

            <div style={S.actionGrid}>
              <ActionCard
                active={action === "retain"}
                title="Retain"
                badge="Keep both"
                text="Both profiles stay separate. Case is closed as reviewed and no profile data is moved."
                onClick={() => canResolve && setAction("retain")}
              />
              <ActionCard
                active={action === "merge"}
                title="Merge"
                badge="Keep existing"
                text="Existing duplicate profile stays canonical. Primary profile data moves into it and the primary profile is hidden from search."
                onClick={() => canResolve && setAction("merge")}
              />
              <ActionCard
                active={action === "replace"}
                title="Replace"
                badge="Keep new"
                text="Primary profile stays canonical. Duplicate profile data moves into it and the duplicate profile is hidden from search."
                onClick={() => canResolve && setAction("replace")}
              />
            </div>

            <div style={S.flowBox}>
              <p style={S.flowTitle}>Resolution flow</p>
              <ul style={S.flowList}>
                {flow.map((line, index) => <li key={index}>{line}</li>)}
              </ul>
            </div>

            <label style={S.noteLabel}>
              Review note
              <textarea
                style={S.textarea}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Example: Confirmed same profile after phone verification..."
                disabled={!canResolve}
              />
            </label>

            <label style={S.confirmBox}>
              <input
                type="checkbox"
                checked={confirmed}
                disabled={!action || !canResolve}
                onChange={(e) => setConfirmed(e.target.checked)}
              />
              <span>I reviewed the evidence and confirm this resolution.</span>
            </label>

            <button
              type="button"
              style={S.submitBtn}
              onClick={onSubmit}
              disabled={!canResolve || !action || !confirmed || saving}
            >
              {saving ? "Saving..." : "Resolve Case"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, tone }) {
  const colors = {
    rose: [C.accentLight, C.accentDark],
    blue: [C.blueLight, C.blue],
    green: [C.greenLight, C.green],
    amber: [C.amberLight, C.amber],
    slate: [C.blueLight, C.text],
  };
  const [bg, color] = colors[tone] || colors.slate;

  return (
    <div style={S.statCard}>
      <div style={{ ...S.statIcon, background: bg, color }}>{value}</div>
      <span>{label}</span>
    </div>
  );
}

function ProfileMini({ label, profileNo, name }) {
  return (
    <div style={S.profileMini}>
      <small>{label}</small>
      <b>#{formatProfileNumberValue(profileNo, "", "—")}</b>
      <span>{name || "Unnamed profile"}</span>
    </div>
  );
}

function ProfileLarge({ label, profileNo, name, id }) {
  return (
    <article style={S.profileLarge}>
      <p>{label}</p>
      <h3>#{formatProfileNumberValue(profileNo, "", "—")}</h3>
      <h2>{name || "Unnamed profile"}</h2>
      <small>{id}</small>
    </article>
  );
}

function ActionCard({ active, title, badge, text, onClick }) {
  return (
    <button
      type="button"
      style={{ ...S.actionCard, ...(active ? S.actionCardActive : {}) }}
      onClick={onClick}
    >
      <div style={S.actionTop}>
        <b>{title}</b>
        <span>{badge}</span>
      </div>
      <p>{text}</p>
    </button>
  );
}

function normalizeStatus(status) {
  return String(status || "pending").trim().toLowerCase();
}

function formatStatus(status) {
  const value = normalizeStatus(status);
  return value.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

function statusStyle(status) {
  const value = normalizeStatus(status);
  if (["merge", "merged"].includes(value)) return { background: C.blueLight, color: C.blue };
  if (["retain", "retained"].includes(value)) return { background: C.greenLight, color: C.green };
  if (["replace", "replaced"].includes(value)) return { background: C.amberLight, color: C.amber };
  return { background: C.accentLight, color: C.accentDark };
}

function getFlow(action) {
  if (action === "retain") {
    return [
      "Case status becomes retained.",
      "Both profiles remain active and separate.",
      "No child tables are moved.",
      "No profile_merge_history row is created.",
    ];
  }

  if (action === "merge") {
    return [
      "Duplicate profile is canonical_profile_id.",
      "Primary profile is merged_profile_id.",
      "Contacts, notes, media, recommendations, alerts, and payments move to the canonical profile.",
      "profile_merge_history stores merge snapshot.",
      "Merged profile gets merge_status='merged'.",
    ];
  }

  if (action === "replace") {
    return [
      "Primary profile becomes canonical replacement.",
      "Duplicate profile becomes merged profile.",
      "Contacts, notes, media, recommendations, alerts, and payments move to the canonical profile.",
      "profile_merge_history stores rollback snapshot.",
      "Old profile gets merge_status='merged'.",
    ];
  }

  return ["Select a resolution action to see the flow."];
}

function formatDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString();
}

const S = {
  page: {
    minHeight: "calc(100vh - 86px)",
    padding: "4px 0 28px",
    color: C.text,
  },

  hero: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 20,
    padding: 28,
    borderRadius: 32,
    background: "#fff",
    border: `1px solid ${C.border}`,
    boxShadow: `0 18px 52px ${C.shadow}`,
    marginBottom: 18,
  },

  eyebrow: {
    margin: "0 0 8px",
    color: C.accent,
    fontSize: 11,
    letterSpacing: 2.5,
    textTransform: "uppercase",
    fontWeight: 950,
  },

  title: {
    margin: 0,
    fontSize: 34,
    fontWeight: 950,
    letterSpacing: -1,
  },

  sub: {
    margin: "10px 0 0",
    color: C.muted,
    fontSize: 14,
    lineHeight: 1.6,
    maxWidth: 820,
  },

  heroActions: {
    display: "flex",
    gap: 10,
  },

  ghostBtn: {
    height: 44,
    padding: "0 18px",
    borderRadius: 999,
    border: `1px solid ${C.borderStrong}`,
    background: "#fff",
    color: C.text,
    cursor: "pointer",
    fontWeight: 950,
  },

  policyNotice: {
    marginBottom: 18,
    padding: "14px 18px",
    borderRadius: 22,
    border: `1px solid ${C.border}`,
    background: C.accentLight,
    boxShadow: `0 10px 28px ${C.shadowLight}`,
  },

  policyNoticeTitle: {
    display: "block",
    color: C.text,
    fontSize: 14,
    fontWeight: 950,
    marginBottom: 6,
  },

  policyNoticeBody: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    color: C.muted,
    fontSize: 13,
    lineHeight: 1.5,
  },

  statGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
    gap: 14,
    marginBottom: 18,
  },

  statCard: {
    background: "#fff",
    border: `1px solid ${C.border}`,
    borderRadius: 24,
    padding: 16,
    boxShadow: `0 12px 34px ${C.shadowLight}`,
    display: "flex",
    alignItems: "center",
    gap: 12,
    color: C.muted,
    fontWeight: 850,
  },

  statIcon: {
    width: 44,
    height: 44,
    borderRadius: 16,
    display: "grid",
    placeItems: "center",
    fontWeight: 950,
    fontSize: 17,
  },

  panel: {
    background: "#fff",
    border: `1px solid ${C.border}`,
    borderRadius: 30,
    padding: 18,
    boxShadow: `0 18px 52px ${C.shadow}`,
  },

  tabs: {
    display: "inline-flex",
    gap: 6,
    padding: 5,
    background: C.blueLight,
    borderRadius: 999,
    marginBottom: 16,
  },

  tabBtn: {
    height: 38,
    padding: "0 16px",
    borderRadius: 999,
    border: "none",
    background: "transparent",
    color: C.muted,
    fontWeight: 950,
    cursor: "pointer",
  },

  tabActive: {
    background: "#fff",
    color: C.text,
    boxShadow: `0 8px 20px ${C.shadowAccent}`,
  },

  toolbar: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 16,
  },

  searchWrap: {
    display: "flex",
    gap: 10,
    flex: 1,
  },

  input: {
    flex: 1,
    minHeight: 46,
    borderRadius: 16,
    border: `1px solid ${C.border}`,
    padding: "0 14px",
    fontWeight: 800,
    outline: "none",
    color: C.text,
    background: "#fff",
  },

  searchBtn: {
    border: "none",
    borderRadius: 999,
    background: `linear-gradient(135deg, ${C.accent}, ${C.accentDark})`,
    color: "#fff",
    padding: "0 18px",
    fontWeight: 950,
    cursor: "pointer",
  },

  select: {
    minHeight: 46,
    borderRadius: 16,
    border: `1px solid ${C.border}`,
    padding: "0 14px",
    background: "#fff",
    fontWeight: 850,
    color: C.text,
  },

  error: {
    padding: 14,
    borderRadius: 18,
    background: C.accentLight,
    color: C.accent,
    fontWeight: 900,
    marginBottom: 14,
  },

  empty: {
    padding: 42,
    textAlign: "center",
    color: C.muted,
    fontWeight: 850,
  },

  cardGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(min(380px, 100%), 1fr))",
    gap: 14,
  },

  caseCard: {
    border: `1px solid ${C.border}`,
    borderRadius: 24,
    padding: 16,
    background: "#fff",
    textAlign: "left",
    cursor: "pointer",
    boxShadow: `0 10px 30px ${C.shadowLight}`,
    color: C.text,
  },

  historyCard: {
    border: `1px solid ${C.border}`,
    borderRadius: 24,
    padding: 16,
    background: "#fff",
    boxShadow: `0 10px 30px ${C.shadowLight}`,
    color: C.text,
  },

  cardTop: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: 14,
    gap: 10,
  },

  statusPill: {
    padding: "6px 10px",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 950,
    textTransform: "uppercase",
  },

  historyPill: {
    padding: "6px 10px",
    borderRadius: 999,
    background: C.blueLight,
    color: C.blue,
    fontSize: 11,
    fontWeight: 950,
    textTransform: "uppercase",
  },

  score: {
    color: C.accent,
    fontSize: 18,
  },

  historyId: {
    color: C.text,
    fontSize: 15,
  },

  profilePair: {
    display: "grid",
    gridTemplateColumns: "1fr auto 1fr",
    gap: 10,
    alignItems: "center",
  },

  arrow: {
    color: C.muted,
    fontWeight: 950,
  },

  profileMini: {
    padding: 12,
    borderRadius: 18,
    background: C.blueLight,
    border: `1px solid ${C.border}`,
    display: "flex",
    flexDirection: "column",
    gap: 4,
    minWidth: 0,
  },

  reason: {
    margin: "14px 0",
    color: C.muted,
    fontSize: 13,
    lineHeight: 1.55,
    minHeight: 42,
  },

  cardFooter: {
    display: "flex",
    justifyContent: "space-between",
    color: C.muted,
    fontSize: 12,
    fontWeight: 800,
  },

  historyMeta: {
    display: "flex",
    flexDirection: "column",
    gap: 5,
    color: C.muted,
    fontSize: 12,
    fontWeight: 800,
    marginTop: 14,
  },

  modalBackdrop: {
    position: "fixed",
    inset: 0,
    zIndex: 999,
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    background: "rgba(37,41,53,0.62)",
    backdropFilter: "blur(10px)",
    padding: "28px 18px",
    overflowY: "auto",
  },

  resolveModal: {
    width: "min(1120px, 100%)",
    background: "#fff",
    borderRadius: 32,
    border: `1px solid ${C.borderStrong}`,
    boxShadow: `0 30px 90px ${C.shadowStrong}`,
    overflow: "hidden",
  },

  modalHead: {
    display: "flex",
    justifyContent: "space-between",
    gap: 16,
    padding: 24,
    borderBottom: `1px solid ${C.border}`,
  },

  modalTitle: {
    margin: 0,
    color: C.text,
    fontSize: 28,
    fontWeight: 950,
    letterSpacing: -0.6,
  },

  modalSub: {
    margin: "8px 0 0",
    color: C.muted,
    lineHeight: 1.55,
    fontSize: 13,
  },

  closeBtn: {
    width: 42,
    height: 42,
    borderRadius: "50%",
    border: `1px solid ${C.borderStrong}`,
    background: "#fff",
    color: C.text,
    fontSize: 24,
    cursor: "pointer",
  },

  modalBody: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) 420px",
    gap: 18,
    padding: 20,
  },

  compareBox: {
    display: "grid",
    gap: 16,
    alignContent: "start",
  },

  profileLarge: {
    padding: 22,
    borderRadius: 24,
    background: C.blueLight,
    border: `1px solid ${C.border}`,
  },

  vsBadge: {
    width: 48,
    height: 48,
    borderRadius: "50%",
    display: "grid",
    placeItems: "center",
    margin: "0 auto",
    background: `linear-gradient(135deg, ${C.accent}, ${C.accentDark})`,
    color: "#fff",
    fontWeight: 950,
  },

  resolvePanel: {
    border: `1px solid ${C.border}`,
    borderRadius: 24,
    padding: 16,
    background: "rgba(255,247,245,0.7)",
  },

  sectionTitle: {
    margin: "0 0 12px",
    color: C.text,
    fontSize: 12,
    fontWeight: 950,
    letterSpacing: 1,
    textTransform: "uppercase",
  },

  actionGrid: {
    display: "grid",
    gap: 10,
  },

  actionCard: {
    border: `1px solid ${C.borderStrong}`,
    borderRadius: 20,
    padding: 14,
    background: "#fff",
    textAlign: "left",
    cursor: "pointer",
    color: C.text,
  },

  actionCardActive: {
    borderColor: C.accent,
    background: `linear-gradient(135deg, ${C.accentLight}, #fff)`,
    boxShadow: `0 0 0 4px ${C.borderSoft}`,
  },

  actionTop: {
    display: "flex",
    justifyContent: "space-between",
    gap: 8,
  },

  flowBox: {
    marginTop: 14,
    padding: 14,
    borderRadius: 18,
    background: "#fff",
    border: `1px solid ${C.border}`,
  },

  flowTitle: {
    margin: "0 0 8px",
    fontSize: 12,
    fontWeight: 950,
    textTransform: "uppercase",
  },

  flowList: {
    margin: 0,
    paddingLeft: 18,
    color: C.muted,
    fontSize: 12,
    lineHeight: 1.65,
    fontWeight: 800,
  },

  noteLabel: {
    display: "block",
    marginTop: 14,
    color: C.text,
    fontSize: 12,
    fontWeight: 950,
    textTransform: "uppercase",
    letterSpacing: 1,
  },

  textarea: {
    marginTop: 8,
    width: "100%",
    minHeight: 92,
    borderRadius: 18,
    border: `1px solid ${C.border}`,
    padding: 12,
    resize: "vertical",
    fontWeight: 750,
    outline: "none",
    boxSizing: "border-box",
  },

  confirmBox: {
    marginTop: 14,
    display: "flex",
    gap: 10,
    padding: 14,
    borderRadius: 18,
    background: "#fff",
    border: `1px solid ${C.border}`,
    fontWeight: 850,
    fontSize: 13,
  },

  submitBtn: {
    width: "100%",
    marginTop: 14,
    border: "none",
    borderRadius: 999,
    background: `linear-gradient(135deg, ${C.accent}, ${C.accentDark})`,
    color: "#fff",
    minHeight: 48,
    fontWeight: 950,
    cursor: "pointer",
  },
};
